import { createColumnHelper } from "@tanstack/react-table";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Popup } from "semantic-ui-react";

import { DATE_FORMATS_FNS, ReactTable } from "components";
import { usePermanentLocations, usePermanentRealtimeDealers } from "components/DealersAndLocationsDropdown/hooks";
import { useManufacturerDropdownOptions } from "modules/ManufacturerDashboard/hooks";
import { formatDate } from "utils/dateHelperFunctions";

import type { Pin } from "models";

type ManufacturerClaimsTableProps = {
  claimsResults: Pin[];
  onSelectRow: (row: Pin) => void;
};

const columnHelper = createColumnHelper<Pin>();

export const ManufacturerClaimsTable = ({ claimsResults, onSelectRow }: ManufacturerClaimsTableProps) => {
  const { t } = useTranslation();

  const { dealers } = usePermanentRealtimeDealers();
  const { locations } = usePermanentLocations();
  const { warrantyDropdownOptions, pinTypeDropdownOptions, pinStatusDropdownOptions } = useManufacturerDropdownOptions();

  const columns = useMemo(
    () => [
      columnHelper.accessor(row => row, {
        id: "reg_num",
        header: `${t("v8_reg", "Reg")} #`,
        cell: ({
          row: {
            original: { reg_number }
          }
        }) => <Popup basic content={reg_number} trigger={<p className="ellipsis">{reg_number}</p>} />
      }),

      columnHelper.accessor(row => row, {
        id: "dealer_id",
        header: t("v8_dealer", "Dealer"),
        cell: ({
          row: {
            original: { dealer_id }
          }
        }) => {
          const matchingDealer = dealers.find(({ id }) => id === dealer_id)?.name || "";
          return <Popup basic content={matchingDealer} trigger={<p className="ellipsis">{matchingDealer}</p>} />;
        }
      }),
      columnHelper.accessor(row => row, {
        id: "location_id",
        header: t("v8_location", "Location"),
        cell: ({
          row: {
            original: { dealer_location_id }
          }
        }) => {
          const location = locations.find(({ id }) => id === dealer_location_id)?.name || "";
          return <Popup basic content={location} trigger={<p className="ellipsis">{location}</p>} />;
        }
      }),
      columnHelper.accessor(row => row, {
        id: "pin_date",
        header: t("v8_pin_date", "Pin Date"),
        cell: ({
          row: {
            original: { created_on }
          }
        }) => {
          const formattedDate = formatDate(created_on, DATE_FORMATS_FNS.dateMonthYear);
          return <Popup basic content={formattedDate} trigger={<p className="ellipsis">{formattedDate}</p>} />;
        }
      }),
      columnHelper.accessor(row => row, {
        id: "pin_type",
        header: t("v8_pin_type", "Pin Type"),
        cell: ({
          row: {
            original: { pin_type_id }
          }
        }) => {
          const matchingPinStatus = pinTypeDropdownOptions.find(({ value }) => value === pin_type_id)?.text ?? "";
          return <Popup basic content={matchingPinStatus} trigger={<p className="ellipsis">{matchingPinStatus}</p>} />;
        }
      }),
      columnHelper.accessor(row => row, {
        id: "warranty_type",
        header: () => t("v8_warranty", "Warranty"),
        cell: ({
          row: {
            original: { warranty_type_id }
          }
        }) => <span>{warrantyDropdownOptions.find(({ value }) => value === warranty_type_id)?.text ?? ""}</span>
      }),
      columnHelper.accessor(row => row, {
        id: "pin_status",
        header: t("v8_pin_status", "Pin Status"),
        cell: ({
          row: {
            original: { pin_status_id }
          }
        }) => {
          const matchingPinStatus = pinStatusDropdownOptions.find(({ value }) => value === pin_status_id)?.text ?? "";
          return <Popup basic content={matchingPinStatus} trigger={<p className="ellipsis">{matchingPinStatus}</p>} />;
        }
      }),
      columnHelper.accessor(row => row, {
        id: "support_nr",
        header: `${t("v8_support", "Support")} #`,
        cell: ({
          row: {
            original: { support_nr }
          }
        }) => <span>{support_nr}</span>
      }),
      columnHelper.accessor(row => row, {
        id: "claim_nr",
        header: ` ${t("v8_claim", "Claim")} # `,
        cell: ({
          row: {
            original: { claim_nr }
          }
        }) => <Popup basic content={claim_nr} trigger={<p className="ellipsis">{claim_nr}</p>} />
      }),
      columnHelper.accessor(row => row, {
        id: "ref_nr",
        header: t("v8_ref") + " #" || "Ref #",
        cell: ({
          row: {
            original: { ref_nr }
          }
        }) => <Popup basic content={ref_nr} trigger={<p className="ellipsis">{ref_nr}</p>} />
      })
    ],
    [t, dealers, locations, pinTypeDropdownOptions, warrantyDropdownOptions, pinStatusDropdownOptions]
  );

  return <ReactTable columns={columns} data={claimsResults} onRowClick={row => onSelectRow(row)} />;
};
